import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Image, Button, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import eyeIcon from '../../../assets/eye_icon.svg';
import styles from './ResetPasswordModal.module.scss';
import { resetPasswordSchema } from '../../../validations/yupValidations';

function ResetPasswordModal({ show, context, handleClose, onConfirm, onCancel }) {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(resetPasswordSchema),
  });

  const handleToggleNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleFormSubmit = async (data) => {
    setSubmitting(true);
    try {
      await onConfirm(data.newPassword);
      reset();
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = (e) => {
    reset();
    onCancel(e);
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        handleClose();
        reset();
      }}
      centered
      backdrop="static"
      keyboard={false}
      className={styles.resetModal}
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          <i className="fa fa-key me-2" />
          Reset Password
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        <Form onSubmit={handleSubmit(handleFormSubmit)} id="resetPasswordForm">
          <p className={styles.description}>
            {`Reset password for this${context ? ' ' : ''}${context.toLowerCase() || 'user'}`}
          </p>

          <div className={styles.passwordField}>
            <label htmlFor="newPassword" className={styles.passwordLabel}>
              New Password <span>*</span>
            </label>
            <div className={styles.inputContainer}>
              <input
                type={showNewPassword ? 'text' : 'password'}
                id="newPassword"
                className={styles.passwordInput}
                placeholder="Enter new password"
                name="newPassword"
                ref={register('newPassword').ref}
                onChange={register('newPassword').onChange}
                onBlur={register('newPassword').onBlur}
              />
              <button
                type="button"
                className={styles.eyeButton}
                onClick={handleToggleNewPassword}
                tabIndex="0"
                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
              >
                <Image src={eyeIcon} alt="Toggle password visibility" />
              </button>
            </div>
            {errors.newPassword && (
              <p className={styles.errorMessage}>{errors.newPassword.message}</p>
            )}
          </div>

          <div className={styles.passwordField}>
            <label htmlFor="confirmPassword" className={styles.passwordLabel}>
              Confirm Password <span>*</span>
            </label>
            <div className={styles.inputContainer}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                className={styles.passwordInput}
                placeholder="Confirm new password"
                name="confirmPassword"
                ref={register('confirmPassword').ref}
                onChange={register('confirmPassword').onChange}
                onBlur={register('confirmPassword').onBlur}
              />
              <button
                type="button"
                className={styles.eyeButton}
                onClick={handleToggleConfirmPassword}
                tabIndex="0"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                <Image src={eyeIcon} alt="Toggle password visibility" />
              </button>
            </div>
            {errors.confirmPassword && (
              <p className={styles.errorMessage}>{errors.confirmPassword.message}</p>
            )}
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer className={styles.modalFooter}>
        <Button
          variant="secondary"
          onClick={handleCancel}
          disabled={submitting}
          className={styles.cancelButton}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="resetPasswordForm"
          variant="primary"
          disabled={submitting}
          className={styles.resetPasswordBtn}
        >
          {submitting ? (
            <>
              <i className="fa fa-spinner fa-spin me-2" />
              Resetting...
            </>
          ) : (
            <>
              <i className="fa fa-key me-2" />
              Reset Password
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

ResetPasswordModal.propTypes = {
  show: PropTypes.bool.isRequired,
  context: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

ResetPasswordModal.defaultProps = {
  context: '',
};

export default ResetPasswordModal;
